import { useRoute, useRouter } from "vue-router";
import { useDocs } from "./useDocs.js";
import { useProjectDocs } from "./useProjectDocs.js";
import { useProjectsPanel } from "./useProjectsPanel.js";
import { useProjectsList } from "./useProjectsList.js";
import { useProjectsByTab } from "./useProjectsByTab.js";
import { useTopBar } from "./useTopBar.js";

// The same document can be on screen in several independently fetched caches
// at once — a side panel, the Project/Docs page behind it, and the open
// document's TopBar. DocumentCardMenu only mutates the one object it was
// given, so a rename/star/delete from any one of them has to be pushed out
// to the others here, or they'd show stale data until their next reload.
export function useDocumentSync() {
  const route = useRoute();
  const router = useRouter();
  const { docs: standaloneDocs, remove: removeStandaloneDoc } = useDocs();
  const { docs: projectDocs, remove: removeProjectDoc } = useProjectDocs();
  const { renameDoc: renamePanelDoc, setDocFavorite: setPanelDocFavorite, removeDoc: removePanelDoc } = useProjectsPanel();
  const { adjustDocCount } = useProjectsList();
  const { adjustDocCount: adjustTabDocCount } = useProjectsByTab();
  const { title, openDocument } = useTopBar();

  const isOpenDocument = (docId) => route.name === "v2-document" && route.params.docId === docId;

  function patch(docId, fields) {
    for (const list of [standaloneDocs.value, projectDocs.value]) {
      const d = list.find((d) => d.id === docId);
      if (d) Object.assign(d, fields);
    }
    if (openDocument.value?.id === docId) Object.assign(openDocument.value, fields);
  }

  function renamed(docId, name) {
    patch(docId, { name });
    renamePanelDoc(docId, name);
    if (isOpenDocument(docId)) title.value = name;
  }

  function favoriteChanged(docId, isFavorite) {
    patch(docId, { isFavorite });
    setPanelDocFavorite(docId, isFavorite);
  }

  // Deleted, or access given up via Reject Share. If it was the open
  // document there's nothing left to show — fall back to where it was
  // listed: the Docs page for a standalone doc, else its project.
  function removed(docId, project) {
    removeStandaloneDoc(docId);
    removeProjectDoc(docId);
    removePanelDoc(docId);
    // Project cards show a document count — same bookkeeping as Project.vue's
    // own onDocumentDeleted.
    if (project?.id && !project.isSingleton) {
      adjustDocCount(project.id, -1);
      adjustTabDocCount(project.id, -1);
    }
    if (!isOpenDocument(docId)) return;
    if (project?.isSingleton || !project?.id) router.push({ name: "v2-docs" });
    else router.push({ name: "v2-project", params: { projectId: project.id } });
  }

  return { renamed, favoriteChanged, removed };
}
